import React, { ComponentProps, useRef } from 'react';
import random from 'lodash/random';
import styled from 'styled-components';
import { up } from '../../utils/styles/breakpoints';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { IconAdd } from '../icon/IconAdd';
import { IconChecked } from '../icon/IconChecked';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Stack } from '../layout/Stack';
import { Typography } from '../typography/Typography';
import type { CategoryUI } from './types';

const Root = styled(Stack).attrs({
    direction: 'row',
    py: { xs: 1, sm: 3 },
    px: 2.5,
    mb: 1,
    spacing: 1,
    flexWrap: 'wrap',
})`
    width: 100%;
    background-color: var(--background-primary);

    &::-webkit-scrollbar {
        display: none;
    }

    ${up('sm')} {
        background: none;
        position: relative;
    }
`;

interface CategoryPickerItemProps extends ComponentProps<typeof Box> {
    displayName: string;
    isActive: boolean;
}

export function CategoryPickerItem(props: CategoryPickerItemProps) {
    const { displayName, isActive, ...other } = props;
    const t = useTranslation();

    return (
        <Box {...other} py={1} px={1.5} borderRadius={2} border="1px solid var(--divider)">
            <Flex direction="row" spacing={1} alignItems="center">
                <Typography noWrap variant="body" weight="bold">
                    {t(displayName)}
                </Typography>
                {isActive ? <IconChecked /> : <IconAdd />}
            </Flex>
        </Box>
    );
}

export const CategoryPickerItemSkeleton = () => {
    const width = useRef(random(70, 120));

    return (
        <Box px={1.5} py={1} b="1px solid var(--divider)" borderRadius={2}>
            <Flex direction="row" spacing={1} alignItems="center">
                <Skeleton variant="text" height="24px" minWidth={width.current} />
                <Skeleton variant="circular" width="16px" height="16px" />
            </Flex>
        </Box>
    );
};

interface CategoryPickerProps {
    items: CategoryUI[];
    value: string[];
    onChange: (value: string[]) => void;
}

export function CategoryPicker(props: CategoryPickerProps) {
    const { items, value = [], onChange } = props;

    if (items.length === 0) {
        return (
            <Root count={10} renderItem={(index) => <CategoryPickerItemSkeleton key={index} />} />
        );
    }

    return (
        <Root
            count={items.length}
            renderItem={(index) => {
                const item = items[index];

                return (
                    <CategoryPickerItem
                        key={item.displayName}
                        displayName={item.displayName}
                        isActive={value.includes(item.id)}
                        onClick={() => onChange([...value, item.id])}
                    />
                );
            }}
        />
    );
}
